import React, { useState, useEffect } from 'react';
import { Container, AuthService, i18n } from '../../../../symphony';
import useStyles from 'isomorphic-style-loader/useStyles';
import s from './AINavbar.scss';
import { LocalizedLink } from '../../../../shared/public/LocalizedLink/LocalizedLink';
import { IconAvatarFill } from '../../../../shared/public/assets/icons/IconAvatarFill';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { AINavbarStore } from '../../stores/ai-navbar.store';
import { openAIFlow } from '../../../../../client/utils/openAIFlow';
import { AILogo } from '../../../../shared/public/AILogo/AILogo';
import { AINavbarService } from '../../services/ai-navbar.service';

export const AINavbar = () => {
  useStyles(s);
  const { navbarVisible$: navbarVisible } = useContainerData('global', AINavbarStore, ['navbarVisible$']);
  const { openProfileMenuPaywall } = Container.take('global', AINavbarService);
  const authService = Container.take('global', AuthService);
  const isLoggedIn = authService?.isLoggedIn();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopUserMenuOpen, setIsDesktopUserMenuOpen] = useState(false);


  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleDesktopUserMenu = () => {
    setIsDesktopUserMenuOpen(!isDesktopUserMenuOpen);
  };

  const closeDesktopUserMenu = () => {
    setIsDesktopUserMenuOpen(false);
  };

  const handleLogout = () => {
    authService?.logout({ pathname: '/', type: 'internal' });
  };

  const onTryMaxClick = (isMobile: boolean = false) => {
    if (isMobile) {
      closeMobileMenu();
    } else {
      closeDesktopUserMenu();
    }
    openProfileMenuPaywall();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Close desktop user menu if clicking outside
      if (isDesktopUserMenuOpen && !target.closest('.ai-navbar__user-container')) {
        closeDesktopUserMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDesktopUserMenuOpen]);

  return (
    navbarVisible ? (
      <nav className="ai-navbar">
        <div className="ai-navbar__container">
          {/* LOGO SECTION */}
          <div className="ai-navbar__logo">
            <LocalizedLink to="/" className="ai-navbar__logo-link" onClick={closeMobileMenu}>
              <AILogo width={204} height={33} />
            </LocalizedLink>
          </div>

          {/* HAMBURGER BUTTON */}
          <button
            className={`ai-navbar__hamburger ${isMobileMenuOpen ? 'ai-navbar__hamburger--active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="ai-navbar__hamburger-line"></span>
            <span className="ai-navbar__hamburger-line"></span>
            <span className="ai-navbar__hamburger-line"></span>
          </button>

          {/* DESKTOP LINKS SECTION */}
          <div className="ai-navbar__links ai-navbar__links--desktop">
            <LocalizedLink to="/learn" className="ai-navbar__link">
              {i18n.t('web-navigation.nav-item-courses')}
            </LocalizedLink>
            {isLoggedIn && (
              <LocalizedLink to="/leaderboard-league" className="ai-navbar__link">
                {i18n.t('web-navigation.nav-item-leaderboard')}
              </LocalizedLink>
            )}
          </div>

          {/* DESKTOP BUTTONS SECTION */}
          <div className="ai-navbar__buttons ai-navbar__buttons--desktop">
            {isLoggedIn ? (
              <div className="ai-navbar__user-container">
                <div className="ai-navbar__user" onClick={toggleDesktopUserMenu}>
                  <IconAvatarFill />
                </div>
                {isDesktopUserMenuOpen && (
                  <div className="ai-navbar__user-dropdown">
                    <LocalizedLink to="/profile" className="ai-navbar__dropdown-item" onClick={closeDesktopUserMenu}>
                      <span>{i18n.t('web-navigation.go-to-profile')}</span>
                    </LocalizedLink>
                    <LocalizedLink to="/profile?settings=true" className="ai-navbar__dropdown-item" onClick={closeDesktopUserMenu}>
                      <span>{i18n.t('web-navigation.settings')}</span>
                    </LocalizedLink>
                    <a className="ai-navbar__dropdown-item" onClick={() => onTryMaxClick(false)}>
                      <span>{i18n.t('web-navigation.web-navigation.pricing-max')}</span>
                    </a>
                    <LocalizedLink to="/contact" className="ai-navbar__dropdown-item" onClick={closeDesktopUserMenu}>
                      <span>{i18n.t('web-navigation.help')}</span>
                    </LocalizedLink>
                    <button className="ai-navbar__dropdown-item ai-navbar__dropdown-item--button" onClick={handleLogout}>
                      <span>{i18n.t('web-navigation.log-out')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <LocalizedLink to="/users/login" className="ai-home__button ai-home__button--secondary">
                  {i18n.t('web-auth.nav-login')}
                </LocalizedLink>
                <a onClick={openAIFlow} className="ai-home__button ai-home__button--primary">
                  {i18n.t('ai-home.start-learning-ai')}
                </a>
              </>
            )}
          </div>
        </div>

        {/* MOBILE MENU */}
        <div className={`ai-navbar__mobile-menu ${isMobileMenuOpen ? 'ai-navbar__mobile-menu--open' : ''}`}>
          <div className="ai-navbar__mobile-content">
            {/* Mobile Links */}
            <div className="ai-navbar__mobile-links">
              <LocalizedLink to="/learn" className="ai-navbar__mobile-link" onClick={closeMobileMenu}>
                {i18n.t('web-navigation.nav-item-courses')}
              </LocalizedLink>
              {isLoggedIn && (
                <LocalizedLink to="/leaderboard-league" className="ai-navbar__mobile-link" onClick={closeMobileMenu}>
                  {i18n.t('web-navigation.nav-item-leaderboard')}
                </LocalizedLink>
              )}
            </div>

            {/* Mobile Profile Actions */}
            {isLoggedIn && (
              <div className="ai-navbar__mobile-profile-actions">
                <LocalizedLink to="/profile" className="ai-navbar__mobile-profile-item" onClick={closeMobileMenu}>
                  <span>{i18n.t('web-navigation.go-to-profile')}</span>
                </LocalizedLink>
                <LocalizedLink to="/profile?settings=true" className="ai-navbar__mobile-profile-item" onClick={closeMobileMenu}>
                  <span>{i18n.t('web-navigation.settings')}</span>
                </LocalizedLink>
                <a className="ai-navbar__mobile-profile-item" onClick={() => onTryMaxClick(true)}>
                  <span>{i18n.t('web-navigation.web-navigation.pricing-max')}</span>
                </a>
                <LocalizedLink to="/contact" className="ai-navbar__mobile-profile-item" onClick={closeMobileMenu}>
                  <span>{i18n.t('web-navigation.help')}</span>
                </LocalizedLink>
                <button className="ai-navbar__mobile-profile-item ai-navbar__mobile-profile-item--button" onClick={handleLogout}>
                  <span>{i18n.t('web-navigation.log-out')}</span>
                </button>
              </div>
            )}

            {/* Mobile Buttons */}
            <div className="ai-navbar__mobile-buttons">
              {!isLoggedIn && (
                <>
                  <LocalizedLink
                    to="/users/login"
                    className="ai-home__button ai-home__button--secondary ai-home__button--mobile"
                    onClick={closeMobileMenu}
                  >
                    {i18n.t('web-auth.nav-login')}
                  </LocalizedLink>
                  <a
                    className="ai-home__button ai-home__button--primary ai-home__button--mobile"
                    onClick={openAIFlow}
                  >
                    {i18n.t('ai-home.start-learning-ai')}
                  </a>
                </>
              )}
            </div>
          </div>
        </div>

        {/* MOBILE MENU OVERLAY */}
        {isMobileMenuOpen && (
          <div className="ai-navbar__overlay" onClick={closeMobileMenu}></div>
        )}
      </nav>
    ) : null
  );
}; 